import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import { PageHeader, Btn, Input, Field, Select } from "@/shared/components/UI";
import { Plus, Search, X, FileSpreadsheet, Briefcase, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ActionQueueWidget from "@/shared/components/ActionQueueWidget";

const STAGE_COLORS = {
  active: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function Projects({ lifecycle = "active" }) {
  const { user: me } = useAuth();
  const canSeeDrafts = me && (me.role === "admin" || me.role === "management" || me.role === "scope_user");
  const canCreate = me && (me.role === "admin" || me.role === "scope_user");
  const isDraft = lifecycle === "draft";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [csms, setCsms] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client_id: "", csm: "", location: "", description: "", deadline: "", requirements: "", buyer_order_type: "", buyer_order_ref: "" });

  function load() {
    const p = { lifecycle };
    if (q) p.q = q;
    if (status && !isDraft) p.status = status;
    api.get("/projects", { params: p }).then((r) => setItems(r.data));
  }
  useEffect(load, [q, status, lifecycle]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([])); }, []);
  useEffect(() => { api.get("/users", { params: { role: "csm" } }).then((r) => setCsms(r.data)).catch(() => {}); }, []);

  async function save(e) {
    e.preventDefault();
    try {
      const payload = { ...form, assignments: form.csm ? { csm: [form.csm] } : {} };
      delete payload.csm;
      const { data } = await api.post("/projects", payload);
      toast.success(`Created draft ${data.project_code}`);
      setOpen(false);
      setForm({ name: "", client_id: "", csm: "", location: "", description: "", deadline: "", requirements: "", buyer_order_type: "", buyer_order_ref: "" });
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  }

  return (
    <div className="p-6 lg:p-10" data-testid={isDraft ? "drafts-page" : "projects-page"}>
      <PageHeader
        kicker={isDraft ? "BOQ BUILD" : "ACTIVE EXECUTION"}
        title={isDraft ? "Project Drafts" : "Projects"}
      >
        {isDraft && canCreate && (
          <Btn onClick={() => setOpen(true)} data-testid="new-draft-btn">
            <Plus className="w-4 h-4" /> New Draft
          </Btn>
        )}
      </PageHeader>

      {/* Phase 32: Personal action queue (only on active project list) */}
      {!isDraft && (
        <div className="mb-8">
          <ActionQueueWidget compact />
        </div>
      )}

      {/* Helper banner */}
      <div className={`p-4 mb-6 border-l-4 ${isDraft ? "bg-amber-50 border-amber-500" : "bg-blue-50 border-blue-500"}`}>
        <div className="flex items-start gap-3">
          {isDraft
            ? <FileSpreadsheet className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            : <Briefcase className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />}
          <div className="text-sm">
            <div className="font-semibold mb-0.5">
              {isDraft ? "Build the Bill of Quantities here" : "Active execution view"}
            </div>
            <div className="text-zinc-600">
              {isDraft
                ? "Drafts are workspaces to compose your BOQ — add line items with images, walls, dimensions and quantity. Once finalized, hit \"Approve & Activate\" to lock the BOQ and move the project into execution."
                : "Each line item moves through the role pipeline (Designer → Pre-Press → Production → Delivery → Install). Roles only see the actions they're responsible for."
              }
            </div>
          </div>
        </div>
      </div>

      <div className="card-flat p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project code, name, location…" className="pl-10" data-testid="projects-search" />
        </div>
        {!isDraft && (
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="on_hold">On hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center text-zinc-400 py-16">
            <div className="font-mono text-xs uppercase tracking-widest mb-2">{isDraft ? "No drafts yet" : "No active projects"}</div>
            {isDraft && <div className="text-sm">Click <span className="font-semibold">&ldquo;New Draft&rdquo;</span> to start composing a BOQ.</div>}
            {!isDraft && canSeeDrafts && <div className="text-sm">Approve a draft from <Link to="/drafts" className="text-blue-700 hover:underline">Project Drafts</Link> to activate it here.</div>}
            {!isDraft && !canSeeDrafts && <div className="text-sm">No active projects assigned to you yet. Ask your CSM to assign you to one.</div>}
          </div>
        )}
        {items.map((p) => (
          <Link key={p.project_code} to={`/projects/${p.project_code}`}
            className={`card-flat p-5 hover:border-zinc-400 transition-colors block ${isDraft ? "border-l-4 border-l-amber-400" : ""}`}
            data-testid={`project-card-${p.project_code}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="font-mono text-xs tracking-wider">
                {p.utr_no
                  ? <><span className="text-emerald-700 font-semibold">{p.utr_no}</span><span className="text-zinc-400 ml-1.5">· {p.project_code}</span></>
                  : <span className="text-zinc-500">{p.project_code}</span>}
              </div>
              {isDraft ? (
                <span className="text-xs font-mono uppercase px-2 py-0.5 border bg-amber-50 text-amber-700 border-amber-200">DRAFT</span>
              ) : (
                <span className={`text-xs font-mono uppercase px-2 py-0.5 border ${STAGE_COLORS[p.overall_status] || STAGE_COLORS.active}`}>
                  {p.overall_status}
                </span>
              )}
            </div>
            {p.utr_no && (
              <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700"
                   data-testid={`project-card-utr-${p.project_code}`}>
                UPR · {p.utr_no}
              </div>
            )}
            <div className="font-display text-xl font-bold tracking-tight mb-1">{p.name}</div>
            <div className="text-sm text-zinc-600 mb-3">{p.client_name} · {p.location || "—"}</div>
            {!isDraft && (
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <Stage label="Design" v={p.design_status} />
                <Stage label="Prod File" v={p.production_file_status} />
                <Stage label="Production" v={p.production_status} />
                <Stage label="Install" v={p.installation_status} />
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-between text-xs text-zinc-500 font-mono">
              <span>Due: {p.deadline || "—"}</span>
              <span className="inline-flex items-center gap-1">
                {isDraft ? "Continue building" : `${p.progress_pct || 0}%`}
                <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white max-w-lg w-full p-6 space-y-4 border border-zinc-200">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">New Project Draft</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <Field label="Project Name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="project-name-input" />
            </Field>
            <Field label="Client" required>
              <Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} required data-testid="project-client-select">
                <option value="">Select client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Assign CSM (Client Success Manager)" required>
              <Select value={form.csm} onChange={(e) => setForm({ ...form, csm: e.target.value })} required data-testid="project-csm-select">
                <option value="">Select the CSM responsible for this project</option>
                {csms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
              <Field label="Deadline"><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Buyer's Order Type">
                <Select value={form.buyer_order_type}
                        onChange={(e) => setForm({ ...form, buyer_order_type: e.target.value })}
                        data-testid="project-buyer-order-type">
                  <option value="">— none —</option>
                  <option value="client_po">Client PO</option>
                  <option value="email">Email Confirmation</option>
                  <option value="other">Other Reference</option>
                </Select>
              </Field>
              <Field label="Buyer's Order No. / Reference">
                <Input value={form.buyer_order_ref}
                       onChange={(e) => setForm({ ...form, buyer_order_ref: e.target.value })}
                       placeholder="PO number / email ref / text"
                       data-testid="project-buyer-order-ref" />
              </Field>
            </div>
            <Field label="Description">
              <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm" />
            </Field>
            <Field label="Client Requirements">
              <textarea rows="3" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm" />
            </Field>
            <div className="flex gap-2"><Btn type="submit" data-testid="save-project-btn">Create Draft</Btn><Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn></div>
          </form>
        </div>
      )}
    </div>
  );
}

function Stage({ label, v }) {
  return (
    <div className="border border-zinc-200 px-2 py-1">
      <div className="text-zinc-500 uppercase tracking-wider text-[10px]">{label}</div>
      <div className="font-medium uppercase">{v || "—"}</div>
    </div>
  );
}
